import { z } from "zod";

import { fetchWithAuth } from "@/lib/api";
import { parseApiResponse } from "@/lib/api-validation";
import {
  bookSchema,
  valueChartSchema,
  performanceResultSchema,
  twrChartSchema,
  performanceDataSchema,
  currentValuationSnapshotSchema,
} from "@/schemas/portfolio.schema";
import { assetChartSchema, pricePointSchema } from "@/schemas/asset.schema";
import { recordSchema } from "@/schemas/transaction.schema";

import type {
  Record as TradeRecord,
  Book,
  PerformanceData,
  PricePoint,
  ValueChart,
  PerformanceResult,
  TWRChart,
  AssetChart,
  CurrentValuationSnapshot,
} from "@/schemas";

import { API_BASE } from "@/config/api";

// Response schemas for API validation
const booksResponseSchema = z.object({
  books: z.array(bookSchema),
});

const pricePointsArraySchema = z.array(pricePointSchema);
const recordsArraySchema = z.array(recordSchema);

// Books fetching
export const getBooks = async (): Promise<Book[]> => {
  try {
    const response = await fetchWithAuth(`${API_BASE}/books`);
    if (!response.ok) {
      throw new Error(`Failed to fetch books: ${response.statusText}`);
    }
    const data = await response.json();
    const validated = parseApiResponse(
      booksResponseSchema,
      data,
      "getBooks"
    );
    return validated.books || [];
  } catch (error) {
    console.error("Error fetching books:", error);
    return [];
  }
};

// Value chart fetching
export const getValueChart = async (
  bookId: string,
  period: string = "1y"
): Promise<ValueChart | null> => {
  try {
    const url =
      bookId === "global"
        ? `${API_BASE}/value-charts/global?period=${period}`
        : `${API_BASE}/value-charts/book/${bookId}?period=${period}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch value chart: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(valueChartSchema, data, `getValueChart(${bookId})`);
  } catch (error) {
    console.error("Error fetching value chart:", error);
    return null;
  }
};

// Performance result fetching
export const getPerformanceResult = async (
  bookId: string,
  period: string = "1y"
): Promise<PerformanceResult | null> => {
  try {
    const url =
      bookId === "global"
        ? `${API_BASE}/performance/global?period=${period}`
        : `${API_BASE}/performance/book/${bookId}?period=${period}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch performance: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(
      performanceResultSchema,
      data,
      `getPerformanceResult(${bookId})`
    );
  } catch (error) {
    console.error("Error fetching performance result:", error);
    return null;
  }
};

export const getCurrentValuation = async (
  bookId: string,
): Promise<CurrentValuationSnapshot | null> => {
  try {
    const url =
      bookId === "global"
        ? `${API_BASE}/current-valuations/global`
        : `${API_BASE}/current-valuations/book/${bookId}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(
        `Failed to fetch current valuation snapshot: ${response.statusText}`,
      );
    }
    const data = await response.json();
    return parseApiResponse(
      currentValuationSnapshotSchema,
      data,
      `getCurrentValuation(${bookId})`,
    );
  } catch (error) {
    console.error("Error fetching current valuation snapshot:", error);
    return null;
  }
};

// TWR chart fetching
export const getTWRChart = async (
  bookId: string,
  period: string = "1y"
): Promise<TWRChart | null> => {
  try {
    const url =
      bookId === "global"
        ? `${API_BASE}/twr-charts/global?period=${period}`
        : `${API_BASE}/twr-charts/book/${bookId}?period=${period}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch TWR chart: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(twrChartSchema, data, `getTWRChart(${bookId})`);
  } catch (error) {
    console.error("Error fetching TWR chart:", error);
    return null;
  }
};

// Chart data fetching
export const getBookChart = async (
  bookId: string,
  period: string = "1y"
): Promise<PricePoint[] | null> => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE}/books/${bookId}/chart?period=${period}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch chart: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(
      pricePointsArraySchema,
      data,
      `getBookChart(${bookId})`
    );
  } catch (error) {
    console.error("Error fetching book chart:", error);
    return null;
  }
};

export const getGlobalChart = async (
  period: string = "1y"
): Promise<PricePoint[] | null> => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE}/books/global/chart?period=${period}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch global chart: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(pricePointsArraySchema, data, "getGlobalChart");
  } catch (error) {
    console.error("Error fetching global chart:", error);
    return null;
  }
};

// Performance data fetching
export const getBookPerformance = async (
  bookId: string,
  period: string = "1y"
): Promise<PerformanceData | null> => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE}/books/${bookId}/performance?period=${period}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch performance: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(
      performanceDataSchema,
      data,
      `getBookPerformance(${bookId})`
    );
  } catch (error) {
    console.error("Error fetching book performance:", error);
    return null;
  }
};

export const getGlobalPerformance = async (
  period: string = "1y"
): Promise<PerformanceData | null> => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE}/books/global/performance?period=${period}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(
        `Failed to fetch global performance: ${response.statusText}`
      );
    }
    const data = await response.json();
    return parseApiResponse(performanceDataSchema, data, "getGlobalPerformance");
  } catch (error) {
    console.error("Error fetching global performance:", error);
    return null;
  }
};

// Benchmark chart fetching
export const getBenchmarkChart = async (
  assetId: string,
  period: string = "1y"
): Promise<AssetChart | null> => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE}/charts/${assetId}?period=${period}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(
        `Failed to fetch benchmark chart: ${response.statusText}`
      );
    }
    const data = await response.json();
    return parseApiResponse(
      assetChartSchema,
      data,
      `getBenchmarkChart(${assetId})`
    );
  } catch (error) {
    console.error("Error fetching benchmark chart:", error);
    return null;
  }
};

// Records fetching
export const getRecordsByBookAndAsset = async (
  bookId: string,
  assetId: string
): Promise<TradeRecord[]> => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE}/records/book/${bookId}/asset/${assetId}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error(`Failed to fetch records: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(
      recordsArraySchema,
      data,
      `getRecordsByBookAndAsset(${bookId}, ${assetId})`
    );
  } catch (error) {
    console.error("Error fetching records by asset:", error);
    return [];
  }
};

export const getRecordCount = async (bookId: string): Promise<number> => {
  try {
    const response = await fetchWithAuth(`${API_BASE}/records/book/${bookId}`);
    if (!response.ok) {
      if (response.status === 404 || response.status === 204) {
        return 0;
      }
      throw new Error(`Failed to fetch record count: ${response.statusText}`);
    }

    const data = await response.json();
    const records = parseApiResponse(
      recordsArraySchema,
      data,
      `getRecordCount(${bookId})`,
    );

    return records.length;
  } catch (error) {
    console.error("Error fetching record count:", error);
    return 0;
  }
};

export const getGlobalRecordsByAsset = async (
  assetId: string
): Promise<TradeRecord[]> => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE}/records/global/asset/${assetId}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error(`Failed to fetch global records: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(
      recordsArraySchema,
      data,
      `getGlobalRecordsByAsset(${assetId})`
    );
  } catch (error) {
    console.error("Error fetching global records by asset:", error);
    return [];
  }
};

// Dividend types and services
export interface AssetDividendItem {
  asset_id: string;
  asset_name: string;
  asset_symbol: string;
  amount: number;
  currency_code: string;
  count: number;
}

export interface DividendAggregation {
  period: string;
  total_amount: number;
  by_asset: AssetDividendItem[];
  count: number;
}

export interface DividendsResponse {
  aggregations: DividendAggregation[];
  available_years: number[];
}

// Dividend response schema
const assetDividendItemSchema = z.object({
  asset_id: z.string(),
  asset_name: z.string(),
  asset_symbol: z.string(),
  amount: z.number(),
  currency_code: z.string(),
  count: z.number(),
});

const dividendAggregationSchema = z.object({
  period: z.string(),
  total_amount: z.number(),
  by_asset: z.array(assetDividendItemSchema),
  count: z.number(),
});

const dividendsResponseSchema = z.object({
  aggregations: z.array(dividendAggregationSchema),
  available_years: z.array(z.number()),
});

export const getDividendsReceived = async (
  bookId: string,
  groupBy: "month" | "year" = "month",
  year?: number
): Promise<DividendsResponse> => {
  try {
    const params = new URLSearchParams({ group_by: groupBy });
    if (year) {
      params.append("year", year.toString());
    }
    const response = await fetchWithAuth(
      `${API_BASE}/records/book/${bookId}/dividends?${params}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return { aggregations: [], available_years: [] };
      }
      throw new Error(`Failed to fetch dividends: ${response.statusText}`);
    }
    const data = await response.json();
    return parseApiResponse(
      dividendsResponseSchema,
      data,
      `getDividendsReceived(${bookId})`
    );
  } catch (error) {
    console.error("Error fetching dividends:", error);
    return { aggregations: [], available_years: [] };
  }
};

export const getGlobalDividendsReceived = async (
  groupBy: "month" | "year" = "month",
  year?: number
): Promise<DividendsResponse> => {
  try {
    const params = new URLSearchParams({ group_by: groupBy });
    if (year) {
      params.append("year", year.toString());
    }
    const response = await fetchWithAuth(
      `${API_BASE}/records/global/dividends?${params}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return { aggregations: [], available_years: [] };
      }
      throw new Error(
        `Failed to fetch global dividends: ${response.statusText}`
      );
    }
    const data = await response.json();
    return parseApiResponse(
      dividendsResponseSchema,
      data,
      "getGlobalDividendsReceived"
    );
  } catch (error) {
    console.error("Error fetching global dividends:", error);
    return { aggregations: [], available_years: [] };
  }
};
